/**
 * utils.js — функции для обработки и объединения данных меню из Strapi для Header.
 *
 * transformMenuData — преобразует сырые данные из GraphQL (Strapi) в структуру меню для компонента Header.
 *   - Собирает колонки, элементы меню, фильтрует пустые items.
 *   - Возвращает массив меню с вложенными элементами и колонками.
 *
 * mergeMenusByOrder — объединяет старое и новое меню по порядку (order), чтобы корректно отобразить структуру.
 *   - Используется для поддержки разных источников меню.
 */
const transformMenuData = (allStrapiMenus, allStrapiMenuContentColumns, allStrapiMenuItems) => {
    const columnsMap = (allStrapiMenuContentColumns?.nodes || []).reduce((map, column) => {
        return {...map, [column.strapiId]: column};
    }, {});

    const menuItemsMap = (allStrapiMenuItems?.nodes || []).reduce((map, item) => {
        if (item.strapiId != null) {
            return {...map, [item.strapiId]: item};
        }
        return map;
    }, {});

    return (allStrapiMenus?.nodes || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((menu) => {
            const menuItems = (menu.items || [])
                .map((item) => {
                    const menuItem = item.menu_item || {};

                    const lookupKeys = [menuItem.id, menuItem.strapiId];
                    const foundKey = lookupKeys.find((k) => k != null && menuItemsMap[k]);
                    const menuItemConfig = foundKey ? menuItemsMap[foundKey] : null;

                    const columns = Array.isArray(menuItemConfig?.columns)
                        ? menuItemConfig.columns
                              .map((col) => {
                                  const columnId = col?.main_column?.id ?? col?.right_column?.id;
                                  const contentColumn = columnId != null ? columnsMap[columnId] : null;
                                  if (!contentColumn) return null;

                                  // Нормализуем элементы внутри колонки
                                  const normalizedItems = (contentColumn.items || []).map((ci) => {
                                      return {
                                          id: ci.id ?? ci.strapiId ?? ci.title ?? JSON.stringify(ci),
                                          title: ci.title ?? "",
                                          description: ci.description ?? null,
                                          link: ci.link ?? null,
                                          badge: ci.badge ?? null,
                                          isDisabled: ci.is_disabled ?? false,
                                          icon: ci.icon ?? null,
                                      };
                                  });

                                  return {
                                      title: contentColumn.label,
                                      link: contentColumn.link || null,
                                      items: normalizedItems,
                                      footer: contentColumn.footer || [],
                                  };
                              })
                              .filter(Boolean)
                        : [];

                    // Проверка на "пустой" item
                    const isEmptyItem = !menuItem || Object.values(menuItem).every((v) => v == null);
                    if (isEmptyItem && !menuItemConfig) {
                        return null;
                    }

                    return {
                        id: menuItem.strapiId ?? menuItem.id ?? menuItem.title ?? null,
                        title: menuItem.title ?? null,
                        label: menu.label ?? null,
                        description: menuItem.description ?? null,
                        icon: menuItem.icon ?? null,
                        link: menuItemConfig?.link ?? null,
                        order: menuItem.order ?? item.order ?? 0,
                        columns,
                    };
                })
                .filter(Boolean)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

            // Если menuItems пустой, не добавляем поле items
            const result = {
                title: menu.title,
                menuId: menu.menuId,
                label: menu.label,
                link: menu.link ?? null,
                order: menu.order ?? 0,
            };
            if (menuItems.length > 0) {
                result.items = menuItems;
            }
            return result;
        });
};

const mergeMenusByOrder = (oldMenu = [], newMenu = []) => {
    const sortedNewMenu = [...newMenu].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const result = [];
    let oldMenuIndex = 0;

    sortedNewMenu.forEach((newItem) => {
        const targetPosition = newItem.order ?? 0;

        while (result.length < targetPosition && oldMenuIndex < oldMenu.length) {
            result.push({
                ...oldMenu[oldMenuIndex],
                source: "old",
            });
            oldMenuIndex++;
        }

        result.push({
            ...newItem,
            source: "new",
        });
    });

    while (oldMenuIndex < oldMenu.length) {
        result.push({
            ...oldMenu[oldMenuIndex],
            source: "old",
        });
        oldMenuIndex++;
    }

    return result;
};

export {mergeMenusByOrder, transformMenuData};
